import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

// import ruledDefaultSvg from 'common/icons/RuledDefault';
import SquareDefault from 'common/icons/SquareDefault';
import StubDefault from 'common/icons/StubDefault';

import { containerPaddingS } from 'common/styles/variables';
import {
    getTicketStylesLayout,
    getTicketBackgroundStyles,
    getTicketBorderStyles,
} from 'common/entities/form/styleConfiguration/selectors';
import {
    setTicketStylesLayout,
    setTicketBackgroundColor,
    setTicketBackgroundImageBaseUrl,
    setTicketBackgroundImageUrl,
    setTicketBorderWeight,
    setTicketBorderRadius,
    setTicketBorderSizeTop,
    setTicketBorderSizeBottom,
    setTicketBorderSizeRight,
    setTicketBorderSizeLeft,
    setTicketBorderColorDefault,
    setTicketBorderColorHover,
    setTicketBackgroundOpacity,
} from 'common/entities/form/styleConfiguration/actions';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';
import { getColors } from 'common/entities/color/selectors';
import { TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON } from 'common/entities/ticketType/selectors';
import {
    DEFAULT_TICKET_BACKGROUND_COLOR,
    DEFAULT_TICKET_BORDER_COLOR,
    DEFAULT_TICKET_BORDER_COLOR_HOVER,
    DEFAULT_TICKET_BACKGROUND_OPACITY,
} from 'common/entities/form/styleConfiguration/defaultValues';

import { SuiContainer, SuiLabel } from '@splashthat/sui';
import ColorPickerEditor from 'common/components/ColorPicker';
import BorderEditor from 'common/components/BorderPicker';
import ColorAndImagePicker from 'common/components/ColorAndImagePicker';
import SplashSlider from 'common/components/SplashSlider';
import FormStyleTabsSelector from '../../FormStyleTabsSelector';

import {
    COLOR_PICKER_DEFAULT,
    COLOR_PICKER_HOVER,
    TICKET_BACKGROUND_PANEL_LABEL,
    TICKET_BORDER_PANEL_LABEL,
    TICKET_LABEL,
} from '../../../../constants/formStyleLabels';
import { COLLAPSIBLE_CONTENT, COLLAPSIBLE_SECTION } from '../../../../constants/reusableClassNames';
import { getFormId, getOpenTicketStylingType } from '../../../../selectors';
import { TICKET_QA } from '../../../../constants/ticketDataQa';
import { BACKGROUND_LABEL_QA } from '../../../../utils/qaIdConstants';

const PaddedSeparator = styled.div`
    padding: ${containerPaddingS} 0;
`;

const STYLE_LABEL = 'Ticket Style';

const FormStyleTicketEditorTicketPanel = ({
    openTicketType,
    formId,
    backgroundColor,
    imageUrl,
    imageBaseUrl,
    defaultColor,
    hoverColor,
    borderRadius,
    borderWidths,
    colorList,
    opacity,
    ...props
}) => (
    <SuiContainer className={COLLAPSIBLE_CONTENT} data-qa={`${TICKET_QA}--panel`}>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${TICKET_QA}--section-${BACKGROUND_LABEL_QA}`}>
            <SuiLabel text={STYLE_LABEL} data-qa={`${TICKET_QA}--label`} />
            <FormStyleTabsSelector
                tabs={[
                    {
                        value: 'square',
                        label: 'Square',
                        icon: <SquareDefault />,
                    },
                    { value: 'stub', label: 'Stub', icon: <StubDefault /> },
                ]}
                value={props.style}
                customName={'SPACING_CUSTOM'}
                onChange={(style) => {
                    const setBorder = {
                        square: () => {
                            props.setTicketBorderWeight(formId, 1, openTicketType);
                        },
                        default: () => {
                            props.setTicketBorderSizeLeft(formId, 10, openTicketType);
                            props.setTicketBorderSizeRight(formId, 1, openTicketType);
                            props.setTicketBorderSizeBottom(formId, 1, openTicketType);
                            props.setTicketBorderSizeTop(formId, 1, openTicketType);
                        },
                    };

                    (setBorder[style] || setBorder.default)();
                    props.setTicketStylesLayout(formId, style, openTicketType);
                }}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${TICKET_QA}--section-${BACKGROUND_LABEL_QA}`}>
            <SuiLabel text={TICKET_BACKGROUND_PANEL_LABEL} data-qa={`${TICKET_QA}--label`} />
            <ColorAndImagePicker
                colorList={colorList}
                color={backgroundColor}
                changeColor={(id) => props.setTicketBackgroundColor(formId, id, openTicketType)}
                qaId={`${TICKET_QA}--color-image`}
                imageUrl={imageUrl}
                imageBaseUrl={imageBaseUrl}
                onChangeImage={(baseUrl) => props.setTicketBackgroundImageBaseUrl(formId, baseUrl, openTicketType)}
                onEditImage={(url) => props.setTicketBackgroundImageUrl(formId, url, openTicketType)}
            />
            <PaddedSeparator />
            <SplashSlider
                value={opacity * 100}
                onChange={(value) => props.setTicketBackgroundOpacity(formId, value / 100, openTicketType)}
                qaId={`${TICKET_QA}--opacity`}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${TICKET_QA}--section-${TICKET_BORDER_PANEL_LABEL}`}>
            <SuiContainer className={'BorderColorPicker'}>
                <BorderEditor
                    componentLabel={TICKET_LABEL}
                    borderWidths={borderWidths}
                    onBorderWidthsChanged={(width) => props.setTicketBorderWeight(formId, width, openTicketType)}
                    borderRadius={borderRadius}
                    onBorderRadiusChanged={(radius) => props.setTicketBorderRadius(formId, radius, openTicketType)}
                    onChangeTopBorderWidth={(width) => props.setTicketBorderSizeTop(formId, width, openTicketType)}
                    onChangeBottomBorderWidth={(width) =>
                        props.setTicketBorderSizeBottom(formId, width, openTicketType)
                    }
                    onChangeRightBorderWidth={(width) => props.setTicketBorderSizeRight(formId, width, openTicketType)}
                    onChangeLeftBorderWidth={(width) => props.setTicketBorderSizeLeft(formId, width, openTicketType)}
                    borderOptionsQa={`${TICKET_QA}--borderOptions`}
                    borderDropdownQa={`${TICKET_QA}--borderDropdown`}
                />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={defaultColor}
                    firstColorLabel={COLOR_PICKER_DEFAULT}
                    changeFirstColor={(id) => props.setTicketBorderColorDefault(formId, id, openTicketType)}
                    secondColor={hoverColor}
                    secondColorLabel={COLOR_PICKER_HOVER}
                    changeSecondColor={(id) => props.setTicketBorderColorHover(formId, id, openTicketType)}
                    qaId={TICKET_QA}
                />
            </SuiContainer>
        </SuiContainer>
    </SuiContainer>
);

FormStyleTicketEditorTicketPanel.propTypes = {
    openTicketType: PropTypes.oneOf([TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON]),
    backgroundColor: styleShapePropTypes,
    imageUrl: PropTypes.string,
    style: PropTypes.string,
    imageBaseUrl: PropTypes.string,
    defaultColor: styleShapePropTypes,
    hoverColor: styleShapePropTypes,
    borderRadius: PropTypes.number,
    borderWidths: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        bottom: PropTypes.number,
        left: PropTypes.number,
    }),
    opacity: PropTypes.number,
    formId: PropTypes.string.isRequired,
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    setTicketBackgroundColor: PropTypes.func.isRequired,
    setTicketStylesLayout: PropTypes.func.isRequired,
    setTicketBackgroundImageBaseUrl: PropTypes.func.isRequired,
    setTicketBackgroundImageUrl: PropTypes.func.isRequired,
    setTicketBorderWeight: PropTypes.func.isRequired,
    setTicketBorderRadius: PropTypes.func.isRequired,
    setTicketBorderSizeTop: PropTypes.func.isRequired,
    setTicketBorderSizeBottom: PropTypes.func.isRequired,
    setTicketBorderSizeRight: PropTypes.func.isRequired,
    setTicketBorderSizeLeft: PropTypes.func.isRequired,
    setTicketBorderColorDefault: PropTypes.func.isRequired,
    setTicketBorderColorHover: PropTypes.func.isRequired,
    setTicketBackgroundOpacity: PropTypes.func.isRequired,
};

FormStyleTicketEditorTicketPanel.defaultProps = {
    backgroundColor: getDefaultStyle(DEFAULT_TICKET_BACKGROUND_COLOR),
    imageUrl: '',
    style: 'stub',
    imageBaseUrl: '',
    defaultColor: getDefaultStyle(DEFAULT_TICKET_BORDER_COLOR),
    hoverColor: getDefaultStyle(DEFAULT_TICKET_BORDER_COLOR_HOVER),
    borderRadius: 0,
    borderWidths: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    opacity: DEFAULT_TICKET_BACKGROUND_OPACITY,
    openTicketType: TICKET_TYPE_STANDALONE, // this prop is undefined when sidebar is closing
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const openTicketType = getOpenTicketStylingType(state);

    const { backgroundColor, imageUrl, imageBaseUrl, opacity } = getTicketBackgroundStyles(
        state,
        formId,
        openTicketType,
    );
    const { colors, radius, ...borderWidths } = getTicketBorderStyles(state, formId, openTicketType);

    const { default: defaultColor, hover: hoverColor } = colors;

    return {
        style: getTicketStylesLayout(state, formId, openTicketType),
        openTicketType,
        formId,
        backgroundColor,
        imageUrl,
        imageBaseUrl,
        defaultColor,
        hoverColor,
        borderRadius: radius,
        borderWidths,
        colorList,
        opacity,
    };
};

const mapDispatchToProps = {
    setTicketStylesLayout,
    setTicketBackgroundColor,
    setTicketBackgroundImageBaseUrl,
    setTicketBackgroundImageUrl,
    setTicketBorderWeight,
    setTicketBorderRadius,
    setTicketBorderSizeTop,
    setTicketBorderSizeBottom,
    setTicketBorderSizeRight,
    setTicketBorderSizeLeft,
    setTicketBorderColorDefault,
    setTicketBorderColorHover,
    setTicketBackgroundOpacity,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormStyleTicketEditorTicketPanel);
